import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../config';
import { s } from './TissStyles';
import { opLabels, localLabel, statusColors } from './TissConstants';
import { formatDate, formatDateTime, formatTime, formatCpf, formatDuration, formatTipoAtendimento, downloadXml, parseXmlTag, parseSolicitacaoData } from './TissHelpers';
import { formatarGlosa } from './TissGlosas';
import { StatusBadge, StatusFlowIndicator, SummaryField, XmlViewer, ToastProvider, useToast } from './TissShared';

// ==================== MAIN PAGE ====================

function ProtocoloDetalhesInner() {
  const { local, protocolo } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGuiaIdx, setSelectedGuiaIdx] = useState(0);
  const [exameView, setExameView] = useState('expandidos');
  const [actionLoading, setActionLoading] = useState(null);

  // Timeline XML lazy load
  const [expandedTx, setExpandedTx] = useState(null);
  const [txXml, setTxXml] = useState({});
  const [loadingXml, setLoadingXml] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/tiss/protocolo/${local}/${protocolo}/detalhes`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar detalhes');
    } finally {
      setLoading(false);
    }
  }, [local, protocolo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const guia = data?.guias?.[selectedGuiaIdx] || null;

  // ==================== ACTIONS ====================

  const handleAction = async (action, label) => {
    if (!guia) return;
    setActionLoading(action);
    try {
      const res = await axios.post(`${API_URL}/tiss/guias/${guia.id}/${action}`);
      if (res.data.mensagem) toast[res.data.encontrada === false ? 'info' : 'success'](res.data.mensagem);
      else toast.success(`${label}: ${res.data.status || 'OK'}`);
      await fetchData();
    } catch (err) {
      toast.error(`Erro ao ${label.toLowerCase()}: ${err.response?.data?.error || err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleVerificarElegibilidade = async () => {
    const carteirinha = data?.atendimento?.CARTEIRINHA?.trim() || guia?.paciente_carteirinha;
    if (!carteirinha) { toast.warning('Carteirinha nao encontrada'); return; }
    setActionLoading('elegibilidade');
    try {
      await axios.post(`${API_URL}/tiss/elegibilidade/verificar`, { carteirinha });
      toast.success('Elegibilidade verificada');
      await fetchData();
    } catch (err) {
      toast.error(`Erro elegibilidade: ${err.response?.data?.error || err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadPdf = () => {
    if (!guia) return;
    const token = localStorage.getItem('token');
    window.open(`${API_URL}/tiss/guias/${guia.id}/pdf?token=${token}`, '_blank');
  };

  // ==================== TIMELINE XML LAZY LOAD ====================

  const loadTxXml = async (txId) => {
    if (txXml[txId]) return;
    setLoadingXml(txId);
    try {
      const { data: txData } = await axios.get(`${API_URL}/tiss/transacoes/${txId}`);
      setTxXml(prev => ({ ...prev, [txId]: { xml_request: txData.xml_request, xml_response: txData.xml_response } }));
    } catch (err) {
      console.error('Erro ao carregar XML:', err);
    } finally {
      setLoadingXml(null);
    }
  };

  const handleExpandTx = (txId) => {
    if (expandedTx === txId) { setExpandedTx(null); return; }
    setExpandedTx(txId);
    loadTxXml(txId);
  };

  // ==================== RENDER ====================

  if (loading) {
    return (
      <div style={s.page} className="data-page">
        <div style={{ textAlign: 'center', padding: '80px 20px', color: '#888' }}>
          <div style={{ fontSize: '16px', marginBottom: '8px' }}>Carregando detalhes do protocolo...</div>
          <div style={{ fontSize: '13px', color: '#aaa' }}>{local}/{protocolo}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={s.page} className="data-page">
        <div style={s.breadcrumb}>
          <a href="/" style={s.breadcrumbLink}>Inicio</a> / <a href="/tiss-unimed" style={s.breadcrumbLink}>TISS UNIMED</a> / Protocolo
        </div>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>!</div>
          <div style={{ fontSize: '16px', color: '#c62828', marginBottom: '8px' }}>{error}</div>
          <button style={s.btnSecondary} onClick={() => navigate('/tiss-unimed')}>Voltar</button>
        </div>
      </div>
    );
  }

  const { atendimento: atend, exames, exames_expandidos, validacao, elegibilidade, estagio, comparacao, prevalidacao } = data;
  const hasGuias = data.guias.length > 0;
  const primaryStatus = guia?.status || 'nao_enviado';

  // Parse autorizacao from XML response
  const xmlResp = guia?.xml_response || '';
  const xmlReq = guia?.xml_request || '';
  const respData = xmlResp ? parseSolicitacaoData(xmlReq, xmlResp) : {};
  const autorizacao = xmlResp ? {
    senha: guia?.numero_autorizacao || parseXmlTag(xmlResp, 'senha'),
    dataAutorizacao: parseXmlTag(xmlResp, 'dataAutorizacao'),
    dataValidadeSenha: parseXmlTag(xmlResp, 'dataValidadeSenha'),
    guiaOperadora: guia?.numero_guia_operadora || parseXmlTag(xmlResp, 'numeroGuiaOperadora'),
  } : {};

  // Map servicos autorizados by codigo for cross-reference with exames table
  const servicosMap = {};
  (respData.servicosAutorizados || []).forEach(srv => {
    servicosMap[srv.codigo] = srv;
  });
  const hasResposta = (respData.servicosAutorizados || []).length > 0;

  const sectionTitle = { fontSize: '14px', fontWeight: '600', color: '#1a1a2e', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' };
  const sectionCard = { ...s.card, padding: '20px 24px' };

  return (
    <div style={s.page} className="data-page">
      {/* Breadcrumb */}
      <div style={s.breadcrumb}>
        <a href="/" style={s.breadcrumbLink}>Inicio</a> / <a href="/tiss-unimed" style={s.breadcrumbLink}>TISS UNIMED</a> / Protocolo {local}/{protocolo}
      </div>

      {/* Header */}
      <div style={{ ...s.pageHeader, marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={() => navigate('/tiss-unimed')} style={{ ...s.btnSecondary, display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
            &#8592; Voltar
          </button>
          <div>
            <h1 style={{ ...s.pageTitle, fontSize: '1.3rem' }}>Protocolo {local}/{protocolo}</h1>
            <p style={s.pageSubtitle}>
              {atend?.PACIENTE_NOME || guia?.paciente_nome || 'Paciente desconhecido'}
              {(atend?.CARTEIRINHA || guia?.paciente_carteirinha) && (
                <span style={{ fontFamily: 'monospace', marginLeft: '8px' }}>
                  | {atend?.CARTEIRINHA?.trim() || guia?.paciente_carteirinha}
                </span>
              )}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <StatusBadge status={primaryStatus} />
          {data.guias.length > 0 && (
            <span style={{ fontSize: '11px', color: '#888' }}>
              {data.guias.length} guia{data.guias.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Um painel só para "como está esta guia". Antes eram três caixas empilhadas
          (fluxo + pré-voo + estágio) que diziam coisas próximas e enchiam a tela mesmo
          quando não havia nada de errado. */}
      <PainelSituacao
        status={primaryStatus}
        temGuia={hasGuias}
        guia={guia}
        prevalidacao={prevalidacao}
        estagio={estagio}
        comparacao={comparacao}
      />

      {/* Action buttons */}
      {hasGuias && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {/* Erro de comunicação (504) quase sempre é autorização que a UNIMED concedeu
              e cuja resposta não chegou — reenviar pediria outra. Buscar lá primeiro. */}
          {guia.status === 'erro' && (
            <button style={s.btnPrimary} disabled={!!actionLoading}
              title="Consulta na UNIMED se esta guia já foi autorizada e traz o número e a senha"
              onClick={() => handleAction('recuperar-autorizacao', 'Buscar na UNIMED')}>
              {actionLoading === 'recuperar-autorizacao' ? 'Buscando...' : 'Buscar na UNIMED'}
            </button>
          )}
          {['pendente', 'erro', 'negado'].includes(guia.status) && (
            <button style={guia.status === 'erro' ? s.btnSecondary : s.btnPrimary} disabled={!!actionLoading}
              onClick={() => handleAction('reenviar', 'Reenviar')}>
              {actionLoading === 'reenviar' ? 'Reenviando...' : 'Reenviar'}
            </button>
          )}
          {['enviado', 'autorizado'].includes(guia.status) && (
            <button style={s.btnSecondary} disabled={!!actionLoading}
              onClick={() => handleAction('status', 'Verificar Status')}>
              {actionLoading === 'status' ? 'Verificando...' : 'Verificar Status'}
            </button>
          )}
          {guia.status === 'autorizado' && (
            <button style={s.btnSuccess} disabled={!!actionLoading}
              onClick={() => handleAction('informar-realizado', 'Informar Realizado')}>
              {actionLoading === 'informar-realizado' ? 'Informando...' : 'Informar Realizado'}
            </button>
          )}
          {/* Faturar sem passar pela autorização — é o que o faturamento já faz no portal
              quando a autorização prévia recusa por regra dela (ex.: parasitológico de 3
              amostras, glosa 2516). Só aparece para guia negada e fora de lote. */}
          {['negado', 'erro'].includes(guia.status) && !guia.lote_id && (
            <button style={{ ...s.btnSecondary, color: '#00695c', borderColor: '#00695c' }} disabled={!!actionLoading}
              onClick={() => {
                const ok = window.confirm(
                  'Faturar esta guia sem autorização prévia?\n\n' +
                  'É o mesmo caminho que o faturamento usa no portal da UNIMED: lança direto, ' +
                  'sem pedir autorização antes. Serve quando a autorização recusa por regra dela ' +
                  'mas o exame foi realizado.\n\n' +
                  'A guia vai ao lote sem senha de autorização. Se a operadora glosar na análise, ' +
                  'será preciso recurso.'
                );
                if (ok) handleAction('faturar-direto', 'Faturar sem autorização');
              }}>
              {actionLoading === 'faturar-direto' ? 'Preparando…' : 'Faturar sem autorização'}
            </button>
          )}
          {/* Só o lote impede o cancelamento; 'realizado' fora de lote ainda dá para cancelar */}
          {!guia.lote_id && guia.status !== 'cancelado' && (
            <button style={s.btnDanger} disabled={!!actionLoading}
              onClick={() => {
                if (window.confirm(`Cancelar a guia ${guia.numero_guia_prestador}?\n\nO cancelamento é enviado à UNIMED e a guia sai do faturamento.`)) {
                  handleAction('cancelar', 'Cancelar');
                }
              }}>
              Cancelar
            </button>
          )}
          <button style={s.btnSecondary} onClick={handleDownloadPdf}>Baixar PDF</button>
        </div>
      )}

      {/* Section B: Dados do Atendimento */}
      <div style={sectionCard}>
        <div style={sectionTitle}>Dados do Atendimento</div>
        {atend ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px 20px' }}>
            <SummaryField label="Paciente" value={atend.PACIENTE_NOME} />
            <SummaryField label="CPF" value={formatCpf(atend.PACIENTE_CPF)} mono />
            <SummaryField label="Data Nascimento" value={formatDate(atend.PACIENTE_NASCIMENTO)} />
            <SummaryField label="Data Atendimento" value={formatDate(atend.DATA)} />
            <SummaryField label="Horario" value={formatTime(atend.HORARIO)} />
            <SummaryField label="Local" value={`${localLabel(atend.LOCAL)} (${atend.LOCAL})`} />
            <SummaryField label="Convenio" value={atend.CONVENIO_NOME} />
            <SummaryField label="Carteirinha" value={atend.CARTEIRINHA} mono />
            <SummaryField label="Plano" value={atend.PLANO} />
            <SummaryField label="Medico" value={atend.MEDICO_NOME} />
            <SummaryField label="CRM" value={atend.MEDICO_CRM ? `${atend.MEDICO_CRM}/${atend.MEDICO_UF || ''}` : '-'} mono />
            <SummaryField label="Indicacao Clinica" value={atend.INDIC_CLINICA || 'Exames laboratoriais'} />
            <SummaryField label="Tipo Atendimento" value={formatTipoAtendimento(atend.TIPO_ATENDIMENTO)} />
            <SummaryField label="CID" value={atend.CID} />
            <SummaryField label="Data Pedido" value={formatDate(atend.DATAPEDIDO)} />
          </div>
        ) : (
          <div style={{ padding: '12px 16px', backgroundColor: '#fff3e0', borderRadius: '6px', fontSize: '13px', color: '#e65100', borderLeft: '4px solid #ff9800' }}>
            Atendimento nao encontrado no SQL Server. Os dados abaixo sao do registro TISS apenas.
          </div>
        )}
      </div>

      {/* Section C: Exames / Procedimentos */}
      {(exames?.length > 0 || exames_expandidos?.length > 0) && (
        <div style={sectionCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={sectionTitle}>Exames / Procedimentos</div>
            <div style={s.segmented}>
              <button style={{ ...s.segmentedBtn, ...(exameView === 'originais' ? s.segmentedBtnActive : {}) }}
                onClick={() => setExameView('originais')}>Originais ({exames?.length || 0})</button>
              <button style={{ ...s.segmentedBtn, ...(exameView === 'expandidos' ? s.segmentedBtnActive : {}) }}
                onClick={() => setExameView('expandidos')}>TISS Expandidos ({exames_expandidos?.length || 0})</button>
            </div>
          </div>

          {validacao && !validacao.valido && (
            <div style={{ padding: '10px 14px', backgroundColor: '#fff3e0', borderRadius: '6px', marginBottom: '12px', fontSize: '12px', color: '#e65100', borderLeft: '4px solid #ff9800' }}>
              <strong>{validacao.problemas?.length || 0} exame(s) sem codigo TUSS valido.</strong> Corrija na tabela de precos.
              <ul style={{ margin: '6px 0 0', paddingLeft: '20px' }}>
                {validacao.problemas?.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
          )}

          <table style={s.table}>
            <thead>
              <tr>
                <th style={{ ...s.th, width: '40px' }}>#</th>
                <th style={s.th}>Codigo</th>
                <th style={s.th}>Descricao</th>
                <th style={s.th}>Cod. TUSS</th>
                <th style={{ ...s.th, textAlign: 'right' }} title="Tabela de preços do convênio (SQL Server: TABELALAUDO.PONTOS / SOLICITACAO_EXAMES_GUIA.VALOR)">
                  Valor <span style={{ fontSize: '9px', color: '#999', fontWeight: '400' }}>(tab. preço)</span>
                </th>
                {hasResposta && (
                  <>
                    <th style={{ ...s.th, textAlign: 'center' }}>Retorno UNIMED</th>
                    <th style={{ ...s.th, textAlign: 'right' }} title="Valor retornado pela UNIMED no XML de resposta (servicoAutorizado.valorAutorizado)">
                      Vlr. Autorizado <span style={{ fontSize: '9px', color: '#999', fontWeight: '400' }}>(UNIMED)</span>
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {(exameView === 'originais' ? exames : exames_expandidos)?.map((ex, i) => {
                const srv = servicosMap[ex.CODIGO_PROCEDIMENTO];
                const qtdAut = srv ? parseInt(srv.quantidadeAutorizada || '0') : null;
                const isAutorizado = qtdAut !== null && qtdAut > 0;
                const isNegado = qtdAut !== null && qtdAut === 0;
                const isPendente = srv === undefined && hasResposta;

                return (
                  <tr key={i} style={s.trHover}>
                    <td style={{ ...s.td, color: '#888' }}>{i + 1}</td>
                    <td style={{ ...s.td, fontFamily: 'monospace', fontWeight: '500' }}>{ex.EXAME_CODIGO}</td>
                    <td style={s.td}>{ex.EXAME_DESCRICAO || ex.PROCEDIMENTO_DESCRICAO}</td>
                    <td style={s.td}>
                      {ex.CODIGO_PROCEDIMENTO ? (
                        <span style={{ fontFamily: 'monospace', fontWeight: '500', color: '#2e7d32' }}>{ex.CODIGO_PROCEDIMENTO}</span>
                      ) : (
                        <span style={{ color: '#c62828', fontSize: '12px' }}>sem codigo</span>
                      )}
                    </td>
                    <td style={{ ...s.td, textAlign: 'right', fontFamily: 'monospace' }}>
                      {(() => {
                        const v = Number(ex.VALORFATURADO || ex.VALOR || ex.VALOR_TABELA || 0);
                        return v > 0 ? `R$ ${v.toFixed(2)}` : <span style={{ color: '#999' }}>-</span>;
                      })()}
                    </td>
                    {hasResposta && (
                      <>
                        <td style={{ ...s.td, textAlign: 'center' }}>
                          {isAutorizado && (
                            <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '12px', backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: '600' }}>
                              Autorizado ({srv.quantidadeAutorizada}/{srv.quantidadeSolicitada})
                            </span>
                          )}
                          {isNegado && (
                            <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '12px', backgroundColor: '#ffebee', color: '#c62828', fontWeight: '600' }}>
                              Negado
                            </span>
                          )}
                          {isPendente && (
                            <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '12px', backgroundColor: '#fff3e0', color: '#e65100', fontWeight: '600' }}>
                              Sem retorno
                            </span>
                          )}
                          {!srv && !hasResposta && '-'}
                        </td>
                        <td style={{ ...s.td, textAlign: 'right', fontFamily: 'monospace' }}>
                          {srv?.valorAutorizado != null ? `R$ ${Number(srv.valorAutorizado).toFixed(2)}` : '-'}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={hasResposta ? 4 : 4} style={{ ...s.td, fontWeight: '600', textAlign: 'right', borderTop: '2px solid #e0e0e0' }}>Total</td>
                <td style={{ ...s.td, fontWeight: '700', textAlign: 'right', fontFamily: 'monospace', borderTop: '2px solid #e0e0e0' }}>
                  {(() => {
                    const total = ((exameView === 'originais' ? exames : exames_expandidos) || [])
                      .reduce((sum, ex) => sum + Number(ex.VALORFATURADO || ex.VALOR || ex.VALOR_TABELA || 0), 0);
                    return total > 0 ? `R$ ${total.toFixed(2)}` : '-';
                  })()}
                </td>
                {hasResposta && (
                  <>
                    <td style={{ ...s.td, textAlign: 'center', borderTop: '2px solid #e0e0e0', fontSize: '12px', fontWeight: '600' }}>
                      {(() => {
                        const total = (respData.servicosAutorizados || []).length;
                        const autorizados = (respData.servicosAutorizados || []).filter(sv => parseInt(sv.quantidadeAutorizada || '0') > 0).length;
                        const negados = total - autorizados;
                        return (
                          <span>
                            <span style={{ color: '#2e7d32' }}>{autorizados} aut.</span>
                            {negados > 0 && <span style={{ color: '#c62828', marginLeft: '6px' }}>{negados} neg.</span>}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ ...s.td, fontWeight: '700', textAlign: 'right', fontFamily: 'monospace', borderTop: '2px solid #e0e0e0' }}>
                      R$ {(respData.servicosAutorizados || [])
                        .reduce((sum, sv) => sum + (Number(sv.valorAutorizado) || 0), 0).toFixed(2)}
                    </td>
                  </>
                )}
              </tr>
            </tfoot>
          </table>

          {/* Glosas da resposta */}
          {(respData.motivos || []).length > 0 && (
            <div style={{ marginTop: '12px', padding: '10px 14px', backgroundColor: '#fff3e0', borderRadius: '6px', borderLeft: '4px solid #ff9800', fontSize: '12px' }}>
              <strong style={{ color: '#e65100' }}>Glosas da operadora:</strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: '20px' }}>
                {respData.motivos.map((m, i) => (
                  <li key={i}>
                    <span style={{ fontFamily: 'monospace', fontWeight: '600' }}>{m.codigo}</span>
                    {m.descricao && <span> — {m.descricao}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Section D: Autorizacao */}
      {hasGuias && (guia.status === 'autorizado' || guia.status === 'realizado' || autorizacao.senha) && (
        <div style={{ ...sectionCard, backgroundColor: '#e8f5e9', borderLeft: '4px solid #2e7d32' }}>
          <div style={{ fontWeight: '600', color: '#2e7d32', marginBottom: '8px', fontSize: '14px' }}>Autorizado pela operadora</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
            <SummaryField label="Senha / Autorizacao" value={autorizacao.senha} mono />
            <SummaryField label="Data Autorizacao" value={formatDate(autorizacao.dataAutorizacao)} />
            <SummaryField label="Validade Senha" value={formatDate(autorizacao.dataValidadeSenha)} />
            <SummaryField label="Guia Operadora" value={autorizacao.guiaOperadora} mono />
          </div>
        </div>
      )}
      {hasGuias && guia.status === 'negado' && (
        <div style={{ ...sectionCard, backgroundColor: '#ffebee', borderLeft: '4px solid #c62828' }}>
          <div style={{ fontWeight: '600', color: '#c62828', marginBottom: '4px', fontSize: '14px' }}>Negado pela operadora</div>
          <div style={{ fontSize: '13px', color: '#555' }}>
            {guia.motivo_negativa
              ? formatarGlosa(guia.motivo_negativa, { prefixo: 'Glosa ' })
              : 'Motivo nao informado'}
          </div>
        </div>
      )}
      {hasGuias && guia.status === 'erro' && guia.ultimo_erro && (
        <div style={{ ...sectionCard, backgroundColor: '#fce4ec', borderLeft: '4px solid #c62828' }}>
          <div style={{ fontWeight: '600', color: '#c62828', marginBottom: '4px', fontSize: '14px' }}>Erro no envio</div>
          <div style={{ fontSize: '13px', color: '#555', whiteSpace: 'pre-wrap' }}>{guia.ultimo_erro}</div>
        </div>
      )}

      {/* Section E: Elegibilidade */}
      <div style={sectionCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div style={sectionTitle}>Elegibilidade</div>
          <button style={s.btnSecondary} disabled={actionLoading === 'elegibilidade'} onClick={handleVerificarElegibilidade}>
            {actionLoading === 'elegibilidade' ? 'Verificando...' : 'Verificar Agora'}
          </button>
        </div>
        {elegibilidade ? (
          <div style={{
            padding: '14px 18px', borderRadius: '8px',
            backgroundColor: elegibilidade.elegivel ? '#e8f5e9' : '#ffebee',
            border: `1px solid ${elegibilidade.elegivel ? '#c8e6c9' : '#ffcdd2'}`,
          }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: elegibilidade.elegivel ? '#2e7d32' : '#c62828', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              {elegibilidade.elegivel ? 'Beneficiario Elegivel' : 'Beneficiario Nao Elegivel'}
              {elegibilidade.expirado && (
                <span style={{ fontSize: '11px', fontWeight: '500', color: '#e65100', backgroundColor: '#fff3e0', padding: '2px 8px', borderRadius: '10px' }}>
                  cache expirado
                </span>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
              <SummaryField label="Nome" value={elegibilidade.nome_beneficiario} />
              <SummaryField label="Plano" value={elegibilidade.nome_plano} />
              <SummaryField label="Validade" value={elegibilidade.data_validade} />
            </div>
            {elegibilidade.motivo && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#c62828' }}>Motivo: {elegibilidade.motivo}</div>
            )}
            <div style={{ marginTop: '8px', fontSize: '11px', color: '#888' }}>
              Verificado em: {formatDateTime(elegibilidade.created_at)}
            </div>
          </div>
        ) : (
          <div style={{ fontSize: '13px', color: '#888', padding: '12px 0' }}>
            Nenhuma verificacao de elegibilidade encontrada para esta carteirinha.
          </div>
        )}
      </div>

      {/* Section F: Guia TISS selector + info */}
      {hasGuias && (
        <div style={sectionCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={sectionTitle}>Guia TISS</div>
            {data.guias.length > 1 && (
              <div style={s.segmented}>
                {data.guias.map((g, i) => (
                  <button key={g.id}
                    style={{ ...s.segmentedBtn, ...(selectedGuiaIdx === i ? s.segmentedBtnActive : {}) }}
                    onClick={() => { setSelectedGuiaIdx(i); setExpandedTx(null); }}>
                    {g.tipo_envio === 'manual' ? 'Manual' : g.tipo_envio === 'reenvio' ? 'Reenvio' : g.tipo_envio || `#${i + 1}`} ({g.status})
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px 20px' }}>
            <SummaryField label="Guia Prestador" value={guia.numero_guia_prestador} mono />
            <SummaryField label="Guia Operadora" value={guia.numero_guia_operadora} mono />
            <SummaryField label="Autorizacao" value={guia.numero_autorizacao} mono />
            <SummaryField label="Tipo Envio" value={guia.tipo_envio} />
            <SummaryField label="Tentativas" value={String(guia.tentativas || 0)} />
            <SummaryField label="Enviado em" value={formatDateTime(guia.enviado_em)} />
            <SummaryField label="Criado em" value={formatDateTime(guia.created_at)} />
            <SummaryField label="Atualizado em" value={formatDateTime(guia.updated_at)} />
          </div>
        </div>
      )}

      {/* Empty state: no guias */}
      {!hasGuias && (
        <div style={{ ...sectionCard, textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: '36px', marginBottom: '8px', opacity: 0.3 }}>&#9993;</div>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Nenhuma guia TISS enviada para este protocolo</div>
          <div style={{ fontSize: '12px', color: '#999' }}>O envio pode ser feito pela aba Guias & Lotes</div>
        </div>
      )}

      {/* Section G: Timeline */}
      {hasGuias && guia.transacoes?.length > 0 && (
        <div style={sectionCard}>
          <div style={sectionTitle}>
            Timeline de Eventos ({guia.transacoes.length} evento{guia.transacoes.length !== 1 ? 's' : ''})
          </div>

          <div style={{ position: 'relative', paddingLeft: '28px' }}>
            <div style={{ position: 'absolute', left: '9px', top: '8px', bottom: '8px', width: '2px', backgroundColor: '#e0e0e0' }} />
            {guia.transacoes.map((tx, idx) => {
              const isExpanded = expandedTx === tx.id;
              const isOk = tx.sucesso;
              const dotColor = isOk ? '#2e7d32' : '#c62828';
              const borderColor = isOk ? '#c8e6c9' : '#ffcdd2';
              const httpColor = !tx.http_status ? '#999' : tx.http_status < 300 ? '#2e7d32' : tx.http_status < 400 ? '#e65100' : '#c62828';

              return (
                <div key={tx.id} style={{ position: 'relative', marginBottom: idx < guia.transacoes.length - 1 ? '10px' : '0' }}>
                  <div style={{
                    position: 'absolute', left: '-24px', top: '12px',
                    width: '12px', height: '12px', borderRadius: '50%',
                    backgroundColor: dotColor, border: '2px solid #fff',
                    boxShadow: `0 0 0 2px ${dotColor}33`, zIndex: 1,
                  }} />
                  <div
                    onClick={() => handleExpandTx(tx.id)}
                    style={{
                      borderLeft: `4px solid ${isOk ? '#2e7d32' : '#c62828'}`,
                      backgroundColor: isExpanded ? '#fafafa' : '#fff',
                      border: `1px solid ${borderColor}`,
                      borderLeftWidth: '4px',
                      borderLeftColor: isOk ? '#2e7d32' : '#c62828',
                      borderRadius: '6px', padding: '10px 14px',
                      cursor: 'pointer', transition: 'background-color 0.15s',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>
                          {opLabels[tx.operacao] || tx.operacao}
                        </span>
                        <span style={{
                          ...s.badge, fontSize: '10px', padding: '2px 8px',
                          backgroundColor: isOk ? '#e8f5e9' : '#fce4ec',
                          color: isOk ? '#2e7d32' : '#c62828',
                        }}>
                          {isOk ? 'Sucesso' : 'Erro'}
                        </span>
                        {tx.http_status && (
                          <span style={{ fontSize: '11px', fontFamily: 'monospace', fontWeight: '600', color: httpColor }}>
                            HTTP {tx.http_status}
                          </span>
                        )}
                        {tx.duracao_ms != null && (
                          <span style={{ fontSize: '11px', color: '#888' }}>{formatDuration(tx.duracao_ms)}</span>
                        )}
                      </div>
                      <span style={{ fontSize: '11px', color: '#aaa' }}>{formatDateTime(tx.inicio)}</span>
                    </div>
                    {!isOk && tx.erro && (
                      <div style={{ fontSize: '12px', color: '#c62828', marginTop: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isExpanded ? 'normal' : 'nowrap' }}>
                        {isExpanded ? tx.erro : (tx.erro.length > 120 ? tx.erro.substring(0, 120) + '...' : tx.erro)}
                      </div>
                    )}
                    {isExpanded && (
                      <div style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '10px' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '8px', marginBottom: '10px' }}>
                          <SummaryField label="Operacao" value={opLabels[tx.operacao] || tx.operacao} />
                          <SummaryField label="Inicio" value={formatDateTime(tx.inicio)} />
                          <SummaryField label="Fim" value={formatDateTime(tx.fim)} />
                          <SummaryField label="Duracao" value={formatDuration(tx.duracao_ms)} />
                          <SummaryField label="HTTP Status" value={tx.http_status ? String(tx.http_status) : '-'} mono />
                        </div>
                        {tx.endpoint && (
                          <div style={{ marginBottom: '8px' }}>
                            <div style={{ fontSize: '11px', color: '#888', marginBottom: '2px' }}>Endpoint</div>
                            <div style={{ fontSize: '11px', fontFamily: 'monospace', color: '#555', wordBreak: 'break-all' }}>{tx.endpoint}</div>
                          </div>
                        )}
                        {tx.soap_fault && (
                          <div style={{ marginBottom: '8px' }}>
                            <div style={{ fontSize: '11px', color: '#888', marginBottom: '2px' }}>SOAP Fault</div>
                            <div style={{ fontSize: '12px', color: '#c62828', whiteSpace: 'pre-wrap' }}>{tx.soap_fault}</div>
                          </div>
                        )}
                        {tx.erro && (
                          <div style={{ marginBottom: '8px' }}>
                            <div style={{ fontSize: '11px', color: '#888', marginBottom: '2px' }}>Erro completo</div>
                            <div style={{ fontSize: '12px', color: '#c62828', whiteSpace: 'pre-wrap' }}>{tx.erro}</div>
                          </div>
                        )}
                        {loadingXml === tx.id && (
                          <div style={{ textAlign: 'center', padding: '12px', color: '#888', fontSize: '12px' }}>Carregando XMLs...</div>
                        )}
                        {txXml[tx.id] && (
                          <>
                            {txXml[tx.id].xml_request && <XmlViewer xml={txXml[tx.id].xml_request} title="XML Request" />}
                            {txXml[tx.id].xml_response && <XmlViewer xml={txXml[tx.id].xml_response} title="XML Response" />}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Section H: Lote */}
      {guia?.lote && (
        <div style={sectionCard}>
          <div style={sectionTitle}>Lote</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px 20px' }}>
            <SummaryField label="Numero Lote" value={guia.lote.numero_lote} mono />
            <SummaryField label="Status" value={guia.lote.status} />
            <SummaryField label="Protocolo Operadora" value={guia.lote.numero_protocolo_operadora} mono />
            <SummaryField label="Total Guias" value={String(guia.lote.total_guias || 0)} />
            <SummaryField label="Valor Total" value={guia.lote.valor_total ? `R$ ${Number(guia.lote.valor_total).toFixed(2)}` : '-'} />
            <SummaryField label="Data Envio" value={formatDateTime(guia.lote.enviado_em)} />
          </div>
        </div>
      )}

      {/* Section I: XMLs */}
      {hasGuias && (guia.xml_request || guia.xml_response || guia.xml_realizado) && (
        <div style={sectionCard}>
          <div style={{ ...sectionTitle, marginBottom: '16px' }}>XMLs da Guia</div>
          {guia.xml_request && <XmlViewer xml={guia.xml_request} title="XML Enviado (Request)" />}
          {guia.xml_response && <XmlViewer xml={guia.xml_response} title="XML Resposta (Response)" />}
          {guia.xml_realizado && <XmlViewer xml={guia.xml_realizado} title="XML Realizado (Procedimentos Executados)" />}
          <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
            {guia.xml_request && (
              <button style={{ ...s.btnSecondary, fontSize: '12px' }}
                onClick={() => downloadXml(guia.xml_request, `tiss_request_${local}_${protocolo}.xml`)}>
                Download Request
              </button>
            )}
            {guia.xml_response && (
              <button style={{ ...s.btnSecondary, fontSize: '12px' }}
                onClick={() => downloadXml(guia.xml_response, `tiss_response_${local}_${protocolo}.xml`)}>
                Download Response
              </button>
            )}
            {guia.xml_realizado && (
              <button style={{ ...s.btnSecondary, fontSize: '12px' }}
                onClick={() => downloadXml(guia.xml_realizado, `tiss_realizado_${local}_${protocolo}.xml`)}>
                Download Realizado
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== PAINEL DE SITUAÇÃO ====================
//
// Responde três perguntas em um bloco só, na ordem em que o operador precisa:
//   1. onde esta guia está no ciclo
//   2. o que fazer agora  (a frase única, específica)
//   3. detalhes — recolhidos, porque na maioria das guias não há nada a ver
//
// Antes eram três caixas separadas: a régua de status, o pré-voo e o estágio do dado.
// Cada uma nasceu de um problema real, mas juntas empurravam os botões para baixo da
// dobra e coloriam a tela mesmo numa guia saudável.

// A frase única: o que está pegando AGORA, em ordem de urgência.
function resumirSituacao({ status, temGuia, guia, prevalidacao = [], estagio, comparacao }) {
  const bloqueios = prevalidacao.filter(a => a.severidade === 'bloqueio');
  if (bloqueios.length) {
    return { tom: 'ruim', titulo: bloqueios.length === 1 ? bloqueios[0].titulo : `${bloqueios.length} problemas impedem o envio`,
      frase: bloqueios[0].acao, abrirDetalhes: true };
  }
  if (comparacao?.divergente) {
    return { tom: 'ruim', titulo: 'O sistema mudou depois do envio',
      frase: 'O que foi transmitido à UNIMED não é mais o que está no sistema. Veja a diferença nos detalhes.', abrirDetalhes: true };
  }
  if (status === 'negado') {
    return { tom: 'ruim', titulo: `Negada pela operadora${guia?.motivo_negativa ? ` — glosa ${guia.motivo_negativa}` : ''}`,
      frase: 'Corrija o que a operadora apontou e reenvie, ou fature sem autorização se o exame foi realizado.', abrirDetalhes: true };
  }
  if (status === 'erro') {
    return { tom: 'ruim', titulo: 'Erro no envio', frase: guia?.ultimo_erro || 'Veja o motivo nos detalhes.', abrirDetalhes: true };
  }
  if (!temGuia) {
    return { tom: 'neutro', titulo: 'Ainda não enviada à UNIMED',
      frase: 'Tudo aqui vem do sistema do laboratório e pode ser alterado normalmente.' };
  }
  if (guia?.lote_id) {
    return { tom: 'ok', titulo: `No lote ${guia.lote_id} — aguardando a operadora`,
      frase: 'Os dados estão congelados: alterar no sistema agora não muda o que foi faturado.' };
  }
  if (status === 'realizado') {
    return { tom: 'ok', titulo: 'Pronta para entrar em lote',
      frase: guia?.numero_autorizacao ? `Autorização ${guia.numero_autorizacao}.` : 'Sem senha de autorização — faturamento direto, como no lançamento manual.' };
  }
  if (status === 'autorizado') {
    return { tom: 'ok', titulo: `Autorizada${guia?.numero_autorizacao ? ` — senha ${guia.numero_autorizacao}` : ''}`,
      frase: 'Falta informar o realizado para poder entrar em lote.' };
  }
  return { tom: 'neutro', titulo: estagio?.titulo || 'Em andamento', frase: estagio?.descricao || '' };
}

function PainelSituacao({ status, temGuia, guia, prevalidacao = [], estagio, comparacao }) {
  const resumo = resumirSituacao({ status, temGuia, guia, prevalidacao, estagio, comparacao });
  const [aberto, setAberto] = React.useState(!!resumo.abrirDetalhes);

  const TONS = {
    ok:     { borda: '#2e7d32', titulo: '#1b5e20' },
    ruim:   { borda: '#c62828', titulo: '#c62828' },
    neutro: { borda: '#546e7a', titulo: '#37474f' },
  };
  const t = TONS[resumo.tom];
  const temDetalhe = prevalidacao.length > 0 || comparacao || estagio;

  return (
    <div style={{ ...s.card, padding: '14px 20px', marginBottom: '16px', borderLeft: `4px solid ${t.borda}` }}>
      {temGuia && (
        <div style={{ marginBottom: '10px', opacity: 0.85 }}>
          <StatusFlowIndicator currentStatus={status} />
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: '14px', color: t.titulo }}>{resumo.titulo}</div>
          {resumo.frase && (
            <div style={{ fontSize: '13px', color: '#546e7a', marginTop: '3px', lineHeight: 1.5 }}>{resumo.frase}</div>
          )}
        </div>
        {temDetalhe && (
          <button style={{ ...s.btnSecondary, fontSize: '12px', whiteSpace: 'nowrap' }} onClick={() => setAberto(!aberto)}>
            {aberto ? 'Ocultar detalhes' : 'Detalhes'}
          </button>
        )}
      </div>

      {aberto && temDetalhe && (
        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #eceff1' }}>
          {prevalidacao.length > 0 && <PreValidacao achados={prevalidacao} />}
          {estagio && <EstagioDoDado estagio={estagio} comparacao={comparacao} />}
        </div>
      )}
    </div>
  );
}

// ==================== PRÉ-VOO ====================
// Mostra o que a operadora recusaria se a guia fosse enviada agora, com o que fazer em
// cada caso. Bloqueio em vermelho (vai negar), ajuste em azul (o sistema resolve, mas o
// operador precisa saber), aviso em âmbar.
function PreValidacao({ achados }) {
  const CORES = {
    bloqueio: { bg: '#fdeaea', borda: '#c62828', titulo: '#c62828', rotulo: 'IMPEDE O ENVIO' },
    ajuste:   { bg: '#e8f0fe', borda: '#1565c0', titulo: '#0d47a1', rotulo: 'AJUSTADO PELO SISTEMA' },
    aviso:    { bg: '#fff8e1', borda: '#e65100', titulo: '#e65100', rotulo: 'CONFERIR' },
  };
  const bloqueios = achados.filter(a => a.severidade === 'bloqueio').length;

  return (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ fontSize: '11px', fontWeight: 700, color: '#78909c', letterSpacing: '.5px', marginBottom: '8px' }}>
        ANTES DE ENVIAR
      </div>

      {achados.map((a) => {
        const c = CORES[a.severidade] || CORES.aviso;
        return (
          <div key={a.chave} style={{ background: c.bg, borderLeft: `3px solid ${c.borda}`,
            borderRadius: '4px', padding: '10px 14px', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '10px', fontWeight: 700, color: c.titulo, letterSpacing: '.5px' }}>
                {c.rotulo}
              </span>
              <span style={{ fontWeight: 600, fontSize: '13px', color: '#37474f' }}>{a.titulo}</span>
            </div>
            <div style={{ fontSize: '12px', color: '#546e7a', marginTop: '4px', lineHeight: 1.5 }}>{a.detalhe}</div>
            <div style={{ fontSize: '12px', color: c.titulo, marginTop: '4px', fontWeight: 500 }}>
              → {a.acao}
            </div>
          </div>
        );
      })}

      {bloqueios > 0 && (
        <div style={{ fontSize: '12px', color: '#546e7a', marginTop: '4px' }}>
          Corrija no sistema do laboratório e recarregue esta página — os dados são lidos ao vivo.
        </div>
      )}
    </div>
  );
}

// ==================== ESTÁGIO DO DADO ====================
// Explica de onde vem o que está na tela e até onde ainda dá para alterar. Quando o
// sistema do laboratório mudou depois do envio, mostra exatamente o que divergiu.
function EstagioDoDado({ estagio, comparacao, prevalidacaoAcima = true }) {
  const [aberto, setAberto] = React.useState(false);
  const divergente = comparacao?.divergente;

  const cores = divergente
    ? { bg: '#fdeaea', borda: '#c62828', titulo: '#c62828' }
    : estagio.pode_alterar
      ? { bg: '#f1f8e9', borda: '#558b2f', titulo: '#33691e' }
      : { bg: '#eef2f7', borda: '#37474f', titulo: '#263238' };

  const fmt = (d) => (d ? new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

  return (
    <div style={{ marginTop: prevalidacaoAcima ? '12px' : 0 }}>
      <div style={{ fontSize: '11px', fontWeight: 700, color: '#78909c', letterSpacing: '.5px', marginBottom: '8px' }}>
        ATÉ ONDE DÁ PARA ALTERAR
      </div>
      <div style={{ background: cores.bg, borderLeft: `3px solid ${cores.borda}`, borderRadius: '4px', padding: '10px 14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: '13px', color: cores.titulo, marginBottom: '4px' }}>
              {divergente ? 'O sistema mudou depois do envio' : estagio.titulo}
            </div>
            <div style={{ fontSize: '12px', color: '#455a64', lineHeight: 1.5 }}>
              {divergente
                ? `Enviado à UNIMED em ${fmt(comparacao.congelado_em)} com ${comparacao.total_enviados} exame(s). O sistema hoje tem ${comparacao.total_atuais}. O faturamento segue o que foi enviado — a diferença abaixo NÃO chegou na operadora.`
                : estagio.descricao}
            </div>
            {comparacao && !divergente && (
              <div style={{ fontSize: '11px', color: '#607d8b', marginTop: '6px' }}>
                Enviado em {fmt(comparacao.congelado_em)} · {comparacao.total_enviados} exame(s) · confere com o sistema.
              </div>
            )}
          </div>
          <button style={{ ...s.btnSecondary, fontSize: '11px', whiteSpace: 'nowrap' }} onClick={() => setAberto(!aberto)}>
            {aberto ? 'Fechar' : 'De onde vêm os dados'}
          </button>
        </div>

      {divergente && (
        <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px' }}>
          {comparacao.removidos.length > 0 && (
            <div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#c62828', marginBottom: '4px' }}>
                Foi faturado, mas não está mais no sistema ({comparacao.removidos.length})
              </div>
              {comparacao.removidos.map((p) => (
                <div key={p.codigo} style={{ fontSize: '12px', color: '#455a64' }}>
                  <span style={{ fontFamily: 'monospace' }}>{p.codigo}</span> — {p.descricao || 'sem descrição'}
                </div>
              ))}
            </div>
          )}
          {comparacao.adicionados.length > 0 && (
            <div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#e65100', marginBottom: '4px' }}>
                Está no sistema, mas NÃO foi faturado ({comparacao.adicionados.length})
              </div>
              {comparacao.adicionados.map((p) => (
                <div key={p.codigo} style={{ fontSize: '12px', color: '#455a64' }}>
                  <span style={{ fontFamily: 'monospace' }}>{p.codigo}</span> — {p.descricao || 'sem descrição'}
                </div>
              ))}
              <div style={{ fontSize: '11px', color: '#78909c', marginTop: '4px' }}>
                Para cobrar estes, é preciso uma guia nova — a que já foi não muda.
              </div>
            </div>
          )}
        </div>
      )}

      {aberto && (
        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(0,0,0,0.08)', fontSize: '12px', color: '#455a64', lineHeight: 1.6 }}>
          <div><strong>Paciente, exames e valores</strong> vêm do sistema do laboratório e são lidos <strong>ao vivo</strong>, agora. Se alguém alterar lá, muda aqui.</div>
          <div><strong>Número da guia, autorização, retorno da UNIMED e o XML transmitido</strong> ficam guardados no módulo TISS e <strong>não mudam nunca</strong> — é o registro do que a operadora recebeu.</div>
          <div style={{ marginTop: '6px' }}>
            <strong>Até quando dá para alterar:</strong> enquanto a guia não entra em lote, corrigir no sistema e refazer a guia resolve.
            Depois que o lote é enviado, o faturamento segue o XML: mexer no sistema não altera nada na operadora, só cria divergência.
            Nesse ponto, o caminho é tratar com a UNIMED (glosa, recurso ou nova guia).
          </div>
          {comparacao && (
            <div style={{ marginTop: '6px', color: '#607d8b' }}>
              Comparação feita contra a {comparacao.origem_xml} da guia {comparacao.numero_guia_prestador}
              {comparacao.em_lote ? ` (lote ${comparacao.em_lote})` : ''}. Use os botões de XML no fim da página para ver o conteúdo exato.
            </div>
          )}
          </div>
        )}
      </div>
    </div>
  );
}

// Wrap with ToastProvider
export default function ProtocoloDetalhes() {
  return (
    <ToastProvider>
      <ProtocoloDetalhesInner />
    </ToastProvider>
  );
}
